<template>
  <!-- Main Content -->
  <div class="flex-1 p-4 lg:p-8 bg-[#1F1F1F]">
    <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 lg:mb-8 gap-4">
      <div class="flex items-center">
        <svg class="w-6 h-6 text-[#F5BE32] mr-3" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
        </svg>
        <h1 class="text-2xl lg:text-3xl font-bold text-[#F5BE32]">{{ $t('admin.messages.title') }}</h1>
      </div>
      
      <!-- Search Bar -->
      <div class="relative w-full lg:w-auto">
        <input 
          v-model="searchQuery" 
          @input="handleSearch"
          type="text" 
          :placeholder="$t('admin.messages.searchPlaceholder')" 
          class="px-4 py-2 bg-[#4F4F4F] border border-[#F5BE32] text-white rounded-lg focus:outline-none focus:border-[#E5AE22] w-full lg:w-64"
        />
        <svg class="absolute right-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5BE32]"></div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="bg-red-500 text-white p-4 rounded-lg mb-6">
      {{ error }}
    </div>
    
    <!-- Content Panels -->
    <div v-else class="flex flex-col lg:flex-row gap-4 lg:gap-6 h-[calc(100vh-200px)]">
      <!-- Left Panel - Message List -->
      <div class="w-full lg:w-1/3 bg-[#4F4F4F] rounded-xl p-4 lg:p-5 overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-base lg:text-lg font-semibold text-white">{{ $t('admin.messages.messages') }}</h2>
          <div class="text-xs lg:text-sm text-gray-300">
            {{ messages.length }} {{ $t('admin.messages.messagesCount') }}
          </div>
        </div>
        
        <div v-if="messages.length === 0" class="text-center py-8 text-gray-400">
          {{ $t('admin.messages.noMessagesFound') }}
        </div>
        
        <div v-else class="space-y-3">
          <div 
            v-for="message in messages" 
            :key="message.id"
            @click="selectMessage(message)"
            :class="[
              'rounded-lg p-3 lg:p-4 cursor-pointer transition-colors',
              selectedMessage?.id === message.id 
                ? 'bg-[#F5BE32]' 
                : 'bg-[#4F4F4F] hover:bg-[#1F1F1F]'
            ]"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="flex items-center">
                <h3 :class="[
                  'font-bold text-sm lg:text-base',
                  selectedMessage?.id === message.id ? 'text-[#1F1F1F]' : 'text-white'
                ]">
                  {{ message.name }}
                </h3>
                <!-- Unread indicator -->
                <div 
                  v-if="message.status === 'unread'"
                  class="ml-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                >
                  1
                </div>
              </div>
              <span :class="[
                'text-xs lg:text-sm',
                selectedMessage?.id === message.id ? 'text-[#1F1F1F]' : 'text-gray-300'
              ]">
                {{ message.relativeTime }}
              </span>
            </div>
            <p :class="[
              'text-xs lg:text-sm',
              selectedMessage?.id === message.id ? 'text-[#1F1F1F]' : 'text-gray-300'
            ]">
              {{ message.messagePreview }}
            </p>
          </div>
        </div>

        <!-- Load More Button -->
        <div v-if="hasMore" class="flex justify-center mt-6">
          <button 
            @click="loadMore"
            :disabled="loadingMore"
            class="px-4 lg:px-6 py-2 bg-[#F5BE32] text-[#1F1F1F] rounded-lg hover:bg-[#E5AE22] disabled:opacity-50 text-sm"
          >
            {{ loadingMore ? $t('admin.messages.loading') : $t('admin.messages.loadMore') }}
          </button>
        </div>
      </div>

      <!-- Right Panel - Message Details -->
      <div class="w-full lg:w-2/3 bg-[#4F4F4F] rounded-xl p-4 lg:p-5 overflow-y-auto">
        <div v-if="selectedMessage" class="space-y-4">
          <!-- Message Header -->
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 pb-4 border-b border-[#1F1F1F]">
            <div>
              <h2 class="text-lg lg:text-xl font-semibold text-white mb-2">{{ selectedMessage.name }}</h2>
              <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-300">
                <span>{{ selectedMessage.email }}</span>
                <span v-if="selectedMessage.phone">{{ selectedMessage.phone }}</span>
                <span>{{ selectedMessage.relativeTime }}</span>
              </div>
            </div>
            
            <!-- Status and Actions -->
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <span 
                :class="[
                  'inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full',
                  selectedMessage.status === 'unread' 
                    ? 'bg-red-500 text-white' 
                    : 'bg-green-500 text-white'
                ]"
              >
                {{ selectedMessage.status === 'unread' ? $t('admin.messages.statuses.unread') : $t('admin.messages.statuses.read') }}
              </span>
              
              <div class="flex gap-2">
                <button 
                  v-if="selectedMessage.status === 'unread'"
                  @click="markAsRead(selectedMessage.id)"
                  class="px-3 py-1 bg-[#F5BE32] text-[#1F1F1F] text-xs rounded hover:bg-[#E5AE22] transition-colors"
                >
                  {{ $t('admin.messages.markAsRead') }}
                </button>
                <button 
                  @click="showDeletePopup(selectedMessage.id)"
                  class="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                >
                  {{ $t('admin.common.delete') }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- Message Content -->
          <div class="bg-[#1F1F1F] p-4 rounded-lg">
            <p class="text-white text-sm lg:text-base leading-relaxed whitespace-pre-wrap">{{ selectedMessage.message }}</p>
          </div>
        </div>
        
        <div v-else class="flex items-center justify-center h-full text-gray-400">
          <div class="text-center">
            <svg class="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
            </svg>
            <p class="text-lg">{{ $t('admin.messages.selectMessageToViewDetails') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Popup -->
    <div v-if="showPopup" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div class="bg-[#232323] rounded-lg shadow-lg p-8 max-w-xs w-full text-center">
        <h2 class="text-lg font-bold text-white mb-4">{{ $t('admin.messages.confirmDeleteTitle') }}</h2>
        <p class="text-sm text-gray-300 mb-6">{{ $t('admin.messages.confirmDeleteText') }}</p>
        <div class="flex justify-center gap-4">
          <button
            @click="confirmDelete"
            class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            {{ $t('admin.common.delete') }}
          </button>
          <button
            @click="showPopup = false"
            class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            {{ $t('admin.common.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import adminMessageService from '../services/adminMessageService';
import { getFirestore, doc, deleteDoc } from "firebase/firestore";

export default {
  name: 'AdminMessages',
  data() {
    return {
      messages: [],
      selectedMessage: null,
      loading: false,
      loadingMore: false,
      error: null,
      searchQuery: '',
      searchTimeout: null,
      hasMore: false,
      lastDoc: null,
      showPopup: false,
      deleteId: null,
    }
  },
  async mounted() {
    await this.loadMessages();
  },
  methods: {
    async loadMessages() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = {
          limit: 50
        };
        
        const result = await adminMessageService.fetchMessages(params);
        this.messages = result.messages.map(message => adminMessageService.formatMessageForDisplay(message));
        this.hasMore = result.hasMore;
        this.lastDoc = result.lastDoc;
      } catch (error) {
        this.error = error.message;
        console.error('Error loading messages:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loadingMore) return;
      
      this.loadingMore = true;
      
      try {
        const params = {
          limit: 50,
          startAfter: this.lastDoc
        };
        
        const result = await adminMessageService.fetchMessages(params);
        const newMessages = result.messages.map(message => adminMessageService.formatMessageForDisplay(message));
        this.messages.push(...newMessages);
        this.hasMore = result.hasMore;
        this.lastDoc = result.lastDoc;
      } catch (error) {
        this.error = error.message;
        console.error('Error loading more messages:', error);
      } finally {
        this.loadingMore = false;
      }
    },

    async handleSearch() {
      clearTimeout(this.searchTimeout);
      
      this.searchTimeout = setTimeout(async () => {
        if (this.searchQuery.trim().length < 2) {
          await this.loadMessages();
          return;
        }
        
        try {
          const result = await adminMessageService.searchMessages(this.searchQuery);
          this.messages = result.messages.map(message => adminMessageService.formatMessageForDisplay(message));
          this.hasMore = false;
        } catch (error) {
          this.error = error.message;
          console.error('Error searching messages:', error);
        }
      }, 500);
    },

    selectMessage(message) {
      this.selectedMessage = { ...message };
      
      // Mark as read if unread
      if (message.status === 'unread') {
        this.updateMessageStatus(message.id, 'read');
      }
    },

    async updateMessageStatus(messageId, newStatus) {
      try {
        await adminMessageService.updateMessageStatus(messageId, newStatus);
        
        // Update the message in the local array
        const messageIndex = this.messages.findIndex(message => message.id === messageId);
        if (messageIndex !== -1) {
          this.messages[messageIndex].status = newStatus;
          this.messages[messageIndex] = adminMessageService.formatMessageForDisplay(this.messages[messageIndex]);
        }
        
        // Update selected message if it's the same
        if (this.selectedMessage && this.selectedMessage.id === messageId) {
          this.selectedMessage.status = newStatus;
          this.selectedMessage = adminMessageService.formatMessageForDisplay(this.selectedMessage);
        }
      } catch (error) {
        this.error = error.message;
        console.error('Error updating message status:', error);
      }
    },

    async markAsRead(messageId) {
      await this.updateMessageStatus(messageId, 'read');
    },

    async markAsReplied(messageId) {
      await this.updateMessageStatus(messageId, 'replied');
    },

    showDeletePopup(id) {
      this.deleteId = id;
      this.showPopup = true;
    },
    async confirmDelete() {
      this.showPopup = false;
      if (!this.deleteId) return;
      try {
        // Delete from Firebase Firestore
        const db = getFirestore();
        await deleteDoc(doc(db, "contact_messages", this.deleteId));
        // Remove from local array
        this.messages = this.messages.filter(message => message.id !== this.deleteId);
        if (this.selectedMessage && this.selectedMessage.id === this.deleteId) {
          this.selectedMessage = null;
        }
        this.deleteId = null;
      } catch (error) {
        this.error = error.message;
        console.error('Error deleting message:', error);
      }
    },
    async deleteMessage(messageId) {
      if (!confirm('Are you sure you want to delete this message?')) return;
      
      try {
        await adminMessageService.deleteMessage(messageId, 'Deleted by admin');
        
        // Remove from local array
        this.messages = this.messages.filter(message => message.id !== messageId);
        
        // Clear selection if it's the deleted message
        if (this.selectedMessage && this.selectedMessage.id === messageId) {
          this.selectedMessage = null;
        }
      } catch (error) {
        this.error = error.message;
        console.error('Error deleting message:', error);
      }
    }
  }
}
</script>